/**
 * Dialog „Prompt für Claude Code" (nur Admins): wählt aus, welche Rückmeldungen
 * einer Mail mitgehen, zeigt den fertigen Auftrag und kopiert ihn.
 * Den Text baut claudePrompt() in lib/claudePrompt.ts.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ClipboardCopy, Info, MessageSquare, PenLine, RotateCcw, Terminal } from 'lucide-react';
import { Modal } from '../Modal/Modal';
import { useToast } from '../Toast';
import { inZwischenablage, type PlanungsDetail } from '../../lib/planung';
import { aufgabenTitel, claudePrompt, promptEintraege, type PromptEintrag } from '../../lib/claudePrompt';
import { STANDARD_GRUNDREGELN, standardVorlage, wahlVorlagen } from '../../lib/promptStandard';
import { ladeGrundregeln, ladeVorlagen } from '../../lib/promptVorlagen';

type Props = {
  mail: PlanungsDetail;
  /** Name der Person, die den Prompt erzeugt (steht in der Fußzeile des Prompts) */
  ichName?: string;
  offen: boolean;
  onClose: () => void;
};

/** Ab dieser Länge wird Claude Code der Auftrag unhandlich — dann lieber in Teilen. */
const LANG = 40000;

const zahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;

function Zeile({ e, an, onUm }: { e: PromptEintrag; an: boolean; onUm: () => void }) {
  const Icon = e.art === 'vorschlag' ? PenLine : MessageSquare;
  return (
    <li>
      <label className={`za-cp-zeile${an ? ' za-cp-zeile--an' : ''}`}>
        <input type="checkbox" checked={an} onChange={onUm} />
        <Icon size={15} aria-hidden="true" />
        <span className="za-cp-zeile-text">
          <span className="za-cp-zeile-titel">{e.titel}</span>
          {e.kurz && <span className="za-cp-zeile-kurz">{e.kurz}</span>}
        </span>
        <span className={`badge ${e.offen ? 'badge-warning' : 'badge-muted'}`}>{e.offen ? 'offen' : 'erledigt'}</span>
      </label>
    </li>
  );
}

export function ClaudePromptDialog({ mail, ichName, offen, onClose }: Props) {
  const toast = useToast();
  const alle = useMemo(() => promptEintraege(mail), [mail]);
  const mitText = useMemo(() => alle.filter((e) => e.art !== 'passt'), [alle]);
  const passtAnzahl = alle.length - mitText.length;

  // Offene Rückmeldungen sind vorgewählt, erledigte nicht
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set(mitText.filter((e) => e.offen).map((e) => e.id)));
  const [passt, setPasst] = useState(true);
  const [versionen, setVersionen] = useState(true);
  const [vorgehen, setVorgehen] = useState(true);
  const [ergaenzung, setErgaenzung] = useState('');
  const [kopiert, setKopiert] = useState(false);
  // Von Hand geänderter Text: Er bleibt, auch wenn man nebenan eine Auswahl ändert
  const [handText, setHandText] = useState<string | null>(null);

  // Vorlagen und Grundregeln aus den Einstellungen. Fehlen sie (nicht erreichbar, noch keine angelegt),
  // gilt der eingebaute Standard — der Auftrag steht in jedem Fall.
  const vorlagenAbfrage = useQuery({ queryKey: ['prompt-vorlagen'], queryFn: ladeVorlagen, enabled: offen, staleTime: 30_000 });
  const grundregelnAbfrage = useQuery({ queryKey: ['prompt-grundregeln'], queryFn: ladeGrundregeln, enabled: offen, staleTime: 30_000 });
  const vorlagen = useMemo(() => wahlVorlagen(vorlagenAbfrage.data), [vorlagenAbfrage.data]);
  const [vorlageId, setVorlageId] = useState<string | null>(null);
  const vorlage = vorlagen.find((v) => v.id === vorlageId) ?? standardVorlage(vorlagen);
  const grundregeln = grundregelnAbfrage.data ?? STANDARD_GRUNDREGELN;

  const umschalten = (id: string) => setIds((alt) => {
    const neu = new Set(alt);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    return neu;
  });

  const link = `${window.location.origin}/geplante-mails/${mail.id}`;
  const erzeugt = useMemo(
    () => claudePrompt(
      mail,
      { ids, passt, versionen, vorgehen, anweisungen: vorlage.anweisungen, aendertMail: vorlage.aendert_mail, grundregeln },
      link, ichName, ergaenzung,
    ),
    [mail, ids, passt, versionen, vorgehen, vorlage, grundregeln, link, ichName, ergaenzung],
  );
  const text = handText ?? erzeugt;
  const titel = aufgabenTitel(mail);
  const offenZahl = mitText.filter((e) => e.offen).length;
  const gewaehlt = mitText.filter((e) => ids.has(e.id)).length;
  const zuLang = text.length > LANG;

  async function kopieren() {
    const ok = await inZwischenablage(text);
    if (!ok) { toast.error('Kopieren ging nicht — markiere den Text bitte von Hand und kopiere ihn.'); return; }
    setKopiert(true);
    toast.success('Prompt kopiert — jetzt in Claude Code einfügen.');
    window.setTimeout(() => setKopiert(false), 2500);
  }

  return (
    <Modal isOpen={offen} onClose={onClose} title="Prompt für Claude Code" size="lg">
      <div className="za-cp">
        <p className="za-cp-intro">
          <Terminal size={15} aria-hidden="true" />
          <span>
            Fasst die Rückmeldungen zu dieser Mail als Arbeitsauftrag zusammen. Kopieren und in Claude Code einfügen —
            Claude Code ändert die Mail so, wie es in diesem Projekt üblich ist, und überträgt sie nach Brevo.
          </span>
        </p>

        <section className="za-cp-block" aria-labelledby="za-cp-vorlage">
          <h3 id="za-cp-vorlage" className="za-cp-titel">Was soll passieren?</h3>
          <select
            className="za-eingabe"
            value={vorlage.id}
            onChange={(e) => { setVorlageId(e.target.value); setHandText(null); }}
            aria-label="Vorlage für den Auftrag"
          >
            {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {vorlage.beschreibung && <p className="za-cp-leer">{vorlage.beschreibung}</p>}
          {vorlagenAbfrage.isError && (
            <p className="za-cp-leer" role="status">Die Vorlagen ließen sich nicht laden — es gilt der Standardtext.</p>
          )}
        </section>

        <section className="za-cp-block" aria-labelledby="za-cp-wahl">
          <h3 id="za-cp-wahl" className="za-cp-titel">Was soll hinein?</h3>
          {mitText.length === 0 ? (
            <p className="za-cp-leer">Zu dieser Mail gibt es noch keine Kommentare oder Änderungsvorschläge.</p>
          ) : (
            <>
              <ul className="za-cp-liste">
                {mitText.map((e) => <Zeile key={e.id} e={e} an={ids.has(e.id)} onUm={() => umschalten(e.id)} />)}
              </ul>
              <div className="za-cp-schnell">
                <button type="button" className="za-link-knopf" onClick={() => setIds(new Set(mitText.filter((e) => e.offen).map((e) => e.id)))}>
                  Nur offene ({offenZahl})
                </button>
                <button type="button" className="za-link-knopf" onClick={() => setIds(new Set(mitText.map((e) => e.id)))}>Alle</button>
                <button type="button" className="za-link-knopf" onClick={() => setIds(new Set())}>Keine</button>
              </div>
            </>
          )}
        </section>

        <section className="za-cp-block" aria-labelledby="za-cp-mehr">
          <h3 id="za-cp-mehr" className="za-cp-titel">Dazu</h3>
          <div className="za-cp-optionen">
            {passtAnzahl > 0 && (
              <label className="za-cp-option">
                <input type="checkbox" checked={passt} onChange={(e) => setPasst(e.target.checked)} />
                <span>Wer „Passt so“ gesagt hat ({passtAnzahl})</span>
              </label>
            )}
            <label className="za-cp-option">
              <input type="checkbox" checked={versionen} onChange={(e) => setVersionen(e.target.checked)} />
              <span>Bisherige Änderungen an der Mail ({zahl(mail.versionen.length, 'Version', 'Versionen')})</span>
            </label>
            {vorlage.aendert_mail && (
              <label className="za-cp-option">
                <input type="checkbox" checked={vorgehen} onChange={(e) => setVorgehen(e.target.checked)} />
                <span>So wird eine Mail hier geändert (Regeln, Brevo übertragen)</span>
              </label>
            )}
          </div>
          <label className="za-feldgruppe">
            <span className="za-feldgruppe-label">Eigene Ergänzung (optional)</span>
            <textarea
              className="za-eingabe za-cp-ergaenzung"
              rows={2}
              maxLength={2000}
              value={ergaenzung}
              onChange={(e) => setErgaenzung(e.target.value)}
              placeholder="z. B. Bitte die Uhrzeit überall gleich schreiben."
            />
          </label>
        </section>

        <section className="za-cp-block" aria-labelledby="za-cp-vorschau">
          <h3 id="za-cp-vorschau" className="za-cp-titel">Auftrag</h3>
          <p className="za-cp-aufgabe">
            <span>Titel in Claude Code:</span> <strong>{titel}</strong>
          </p>
          <textarea
            className="za-cp-text"
            rows={14}
            spellCheck={false}
            value={text}
            onChange={(e) => setHandText(e.target.value)}
            aria-label="Fertiger Prompt (änderbar)"
          />
          <p className={`za-cp-stand${zuLang ? ' za-cp-stand--warnung' : ''}`} role="status">
            {zuLang ? <Info size={13} aria-hidden="true" /> : null}
            {text.length.toLocaleString('de-DE')} Zeichen · {zahl(gewaehlt, 'Rückmeldung', 'Rückmeldungen')} ausgewählt
            {handText !== null ? ' · von Hand geändert' : ''}
            {zuLang ? ' — das ist viel; wähle weniger aus und arbeite in zwei Runden.' : ''}
            {handText !== null && (
              <button type="button" className="za-link-knopf" onClick={() => setHandText(null)}>
                <RotateCcw size={12} aria-hidden="true" /> Neu erzeugen
              </button>
            )}
          </p>
        </section>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Schließen</button>
          <button type="button" className="btn btn-primary" onClick={() => { void kopieren(); }} disabled={gewaehlt === 0}>
            {kopiert ? <Check size={16} aria-hidden="true" /> : <ClipboardCopy size={16} aria-hidden="true" />}
            {kopiert ? 'Kopiert' : 'Prompt kopieren'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
